/**
 * CRM 页面共用的字典辅助方法
 *
 * 字典数据（ba_dictionary / ba_dictionary_merchant）后端不做关联、不返回名称，
 * 业务表里只存字典值（userCode）。页面拿到字典列表后，在渲染时按下表关联出名称。
 *
 * 约定：
 *   商机阶段 type = OPPORTUNITY_STAGE   userCode 是阶段值（'100'…'600'）
 *   商机来源 type = PARTNER_ORIGIN      userCode 是来源值
 *   客户类型 type = CUSTOMER_TYPE       userCode 是类型值
 *   所属行业 type = INDUSTRY_TYPE       userCode 是行业值
 *   软件类型 type = CUSTOMER_SOFT_TYPE  userCode 是软件类型值
 *
 * ★ 字典值存的是 userCode（后端只存值不存名称），所以下拉的 :value 一律绑 userCode。
 */

/** 商机阶段字典类型 */
export const DICT_OPPORTUNITY_STAGE = "OPPORTUNITY_STAGE";

/** 客户类型字典类型（ba_business_partner_merchant.customer_type） */
export const DICT_CUSTOMER_TYPE = "CUSTOMER_TYPE";

/** 客户所属行业字典类型（ba_business_partner_merchant.industry_id） */
export const DICT_INDUSTRY_TYPE = "INDUSTRY_TYPE";

/** 客户软件类型字典类型（ba_business_partner_merchant.soft_type） */
export const DICT_CUSTOMER_SOFT_TYPE = "CUSTOMER_SOFT_TYPE";

/** 客户职位字典类型（ba_business_partner_merchant.customer_position，联系人用） */
export const DICT_CUSTOMER_POSITION = "CUSTOMER_POSITION";

/**
 * 是否关键联系人（ba_business_partner_merchant.key_contact）
 *
 * 联系人上的标记：0 否 / 1 是。与是否云客户一样是按语义取值的类型字段（非状态字段），
 * 保持数据库注释的 1/0，不走状态字段的 100 倍数规则。
 */
export const KEY_CONTACT = {
    NO: 0,
    YES: 1,
};

/** 是否关键联系人值 → 文案 */
export function keyContactName(keyContact) {
    if (keyContact === null || keyContact === undefined || keyContact === "") {
        return "";
    }
    return Number(keyContact) === KEY_CONTACT.YES ? "是" : "否";
}

/**
 * 是否云客户（ba_business_partner_merchant.cloud_type）
 *
 * 这是按语义取值的类型字段（非状态字段），保持数据库注释的 1/0，不走状态字段的 100 倍数规则。
 */
export const CLOUD_TYPE = {
    NO: 0,
    YES: 1,
};

/**
 * 往来类型（ba_business_partner_merchant.contact_type）
 *
 * 1 个人（可正常联系的具体人） 0 公司（虚拟主体，只能联系其联系人）。
 * 同理是类型字段，保持 1/0。
 */
export const CONTACT_TYPE = {
    COMPANY: 0,
    PERSON: 1,
};

/** 往来类型值 → 文案 */
export function contactTypeName(contactType) {
    if (Number(contactType) === CONTACT_TYPE.PERSON) {
        return "个人";
    }
    return Number(contactType) === CONTACT_TYPE.COMPANY ? "公司" : "";
}

/** 是否云客户值 → 文案 */
export function cloudTypeName(cloudType) {
    if (cloudType === null || cloudType === undefined || cloudType === "") {
        return "";
    }
    return Number(cloudType) === CLOUD_TYPE.YES ? "是" : "否";
}

/**
 * 往来单位类型，与后端 PartnerType 一一对应
 * 跟进记录靠它区分是商机跟进还是客户跟进（关联 ba_business_partner_merchant 取 partner_type）
 */
export const PARTNER_TYPE = {
    CUSTOMER: 1,
    SUPPLIER: 2,
    OPPORTUNITY: 3,
};

/** 商机来源字典类型 */
export const DICT_PARTNER_ORIGIN = "PARTNER_ORIGIN";

/**
 * 阶段值常量，用于业务判断，不用于展示
 *
 * ★ 以字典实际配置为准（type = OPPORTUNITY_STAGE）：
 *   100 线索    200 初步接洽   300 需求确认   400 方案报价
 *   500 商务谈判 600 成交      9999 已流失
 * 「已流失」是 9999 不是连续值，字典里 index 排最后。
 */
export const STAGE = {
    CLUE: 100,
    INITIAL_CONTACT: 200,
    DEMAND_CONFIRM: 300,
    PROPOSAL_QUOTE: 400,
    BUSINESS_NEGOTIATION: 500,
    DEAL: 600,
    LOST: 9999,
};

/** 阶段值 → el-tag 颜色，非阶段值不命中则返回 undefined（默认蓝） */
export const STAGE_TAG = {
    [STAGE.CLUE]: "info",
    [STAGE.PROPOSAL_QUOTE]: "warning",
    [STAGE.BUSINESS_NEGOTIATION]: "warning",
    [STAGE.DEAL]: "success",
    [STAGE.LOST]: "danger",
};

/**
 * 取阶段对应的标签色
 * @param {Number} stageValue 阶段值
 * @returns {String|undefined}
 */
export function stageTagType(stageValue) {
    return STAGE_TAG[stageValue];
}

/**
 * 按字典值取名称
 * @param {Array} dictList 字典列表（queryUniqueDictionaryList 的返回）
 * @param {String|Number} value 字典值，对应 userCode
 * @returns {String} 名称，找不到返回空串
 */
export function dictName(dictList, value) {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    const dict = (dictList || []).find(d => String(d.userCode) === String(value));
    return dict ? dict.dictName : "";
}

/**
 * 把「按阶段值分组」的统计结果按字典补全成完整阶段列表
 *
 * 后端只返回有数据的阶段（GROUP BY 不出现在结果里就没有该行），
 * 而图表、漏斗、明细表都要展示全部阶段，所以这里以字典为准重建一遍，缺的补 0。
 *
 * @param {Array} dictList 阶段字典列表
 * @param {Array} stageStats 后端返回的阶段统计（只有有数据的阶段）
 * @param {Object} [options]
 * @param {Boolean} [options.excludeClose] 是否剔除关闭阶段（已流失），漏斗层用
 * @param {Function} [options.mapStat] 自定义每行字段映射
 * @returns {Array} 补全后的阶段行
 */
export function fillStageRows(dictList, stageStats, options) {
    const opts = options || {};
    const statMap = {};
    (stageStats || []).forEach(s => statMap[s.stageValue] = s);

    return (dictList || [])
        .filter(d => !(opts.excludeClose && Number(d.userCode) === STAGE.LOST))
        .map(d => {
            const stageValue = Number(d.userCode);
            const stat = statMap[stageValue];
            const row = {
                stageValue: stageValue,
                stageTitle: d.dictName,
                opportunityCount: stat ? (stat.opportunityCount || 0) : 0,
                opportunityAmount: stat ? Number(stat.opportunityAmount || 0) : 0,
                amountRatio: stat ? (stat.amountRatio || 0) : 0,
                countRatio: stat ? (stat.countRatio || 0) : 0,
            };
            return opts.mapStat ? opts.mapStat(row, stat) : row;
        });
}
